import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useDealStore } from '../../lib/stores/dealStore';
import { useClientStore } from '../../lib/stores/clientStore';
import { saveGroupNumbersAsContacts } from '../../lib/utils/saveGroupNumbersAsContacts';
import type { Deal } from '../../lib/types';

/** Contenuto della bozza di accettazione (deals.acceptance_draft). */
export interface AcceptanceDraft {
  step: number;
  client: Record<string, string>;
  whatsapp_group_numbers: string[];
  whatsapp_group_name: string;
  whatsapp_reuse_group_jid: string;
  /** Riuso gruppo: mandare il messaggio di benvenuto dell'automazione? (default true) */
  whatsapp_reuse_send_intro?: boolean;
  pending_signed_contract_url: string | null;
  signing_date: string | null; // ISO date (yyyy-MM-dd)
  updated_at?: string;
}

const CLIENT_FIELDS = [
  'business_name', 'vat_number', 'tax_code', 'sdi_code', 'pec', 'email',
  'email_amministrazione', 'street_address', 'street_number', 'postal_code', 'city', 'province',
] as const;

function safeFileName(name: string): string {
  return name
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
}

/** Carica un PDF firmato nel bucket contracts e ritorna un signed URL (24h). */
async function uploadSignedPdf(deal: Deal, file: File): Promise<string> {
  const organizationId = (deal as any).organization_id || 'shared';
  const fileName = `${organizationId}/contracts/${deal.id}/${safeFileName(file.name)}`;
  const { error: uploadError } = await supabase.storage
    .from('contracts')
    .upload(fileName, file, { upsert: true, contentType: 'application/pdf' });
  if (uploadError) throw uploadError;
  const { data: signed } = await supabase.storage.from('contracts').createSignedUrl(fileName, 60 * 60 * 24);
  if (signed?.signedUrl) return signed.signedUrl;
  const { data: pub } = await supabase.storage.from('contracts').getPublicUrl(fileName);
  return pub.publicUrl;
}

export function useAcceptanceDraft() {
  const { updateDeal, updateDealStatus } = useDealStore();
  const { updateClient } = useClientStore();
  const [busy, setBusy] = useState(false);

  /** Legge la bozza salvata sul deal (o null). */
  const loadDraft = (deal: Deal | null | undefined): AcceptanceDraft | null => {
    const raw = (deal as any)?.acceptance_draft;
    return raw && typeof raw === 'object' ? (raw as AcceptanceDraft) : null;
  };

  /**
   * Salva la bozza: se è stato selezionato un nuovo PDF lo carica, poi scrive
   * deals.acceptance_draft. NON cambia lo stato del deal.
   */
  const saveDraft = async (deal: Deal, draft: AcceptanceDraft, newFile?: File | null): Promise<boolean> => {
    setBusy(true);
    try {
      let pendingUrl = draft.pending_signed_contract_url;
      if (newFile) pendingUrl = await uploadSignedPdf(deal, newFile);
      const payload: AcceptanceDraft = {
        ...draft,
        pending_signed_contract_url: pendingUrl,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('deals').update({ acceptance_draft: payload }).eq('id', deal.id);
      if (error) throw error;
      return true;
    } catch (e) {
      console.error('[useAcceptanceDraft] saveDraft failed:', e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Finalizza l'accettazione: applica i dati cliente, promuove il PDF a
   * signed_contract_url, scrive i dati gruppo WhatsApp, salva i contatti, accetta
   * il deal (ordine anti-race: PDF prima del trigger deal_signed) e pulisce la bozza.
   */
  const complete = async (deal: Deal, draft: AcceptanceDraft, newFile?: File | null): Promise<boolean> => {
    setBusy(true);
    try {
      // PDF firmato definitivo
      let signedUrl = draft.pending_signed_contract_url;
      if (newFile) signedUrl = await uploadSignedPdf(deal, newFile);
      if (!signedUrl) throw new Error('PDF firmato mancante');

      // 1. Dati cliente
      if (deal.client_id) {
        const clientPatch: Record<string, any> = {};
        for (const f of CLIENT_FIELDS) {
          const v = (draft.client?.[f] ?? '').trim();
          clientPatch[f] = v === '' ? null : v;
        }
        if (clientPatch.business_name == null) delete clientPatch.business_name; // non azzerare la ragione sociale
        await updateClient(deal.client_id, clientPatch as any);
      }

      const signingDate = draft.signing_date ? new Date(draft.signing_date) : new Date();
      const cleanedNumbers = (draft.whatsapp_group_numbers ?? []).map((n) => n.trim()).filter((n) => n !== '');

      // 2. FIX race condition: PDF + gruppo PRIMA di signed_at (che fa scattare deal_signed)
      await updateDeal(deal.id, {
        signed_contract_url: signedUrl,
        whatsapp_group_numbers: cleanedNumbers,
        whatsapp_group_name: (draft.whatsapp_group_name ?? '').trim(),
        whatsapp_reuse_group_jid: draft.whatsapp_reuse_group_jid || null,
        // Solo col riuso ha senso: false = niente messaggio di benvenuto su questa
        // offerta; null = vale la regola del passo dell'automazione.
        whatsapp_reuse_send_intro: draft.whatsapp_reuse_group_jid
          ? draft.whatsapp_reuse_send_intro !== false
          : null,
      } as any);

      // 3. Accetta (setta signed_at → trigger deal_signed col PDF già presente)
      await updateDealStatus(deal.id, 'accepted');

      // 4. Corregge la data firma e pulisce la bozza
      await updateDeal(deal.id, {
        signed_at: signingDate.toISOString(),
        closed_at: signingDate.toISOString(),
        acceptance_draft: null,
      } as any);

      // 5. Side-effect post-firma, SENZA bloccare la finestra.
      //
      // A questo punto l'accettazione è già completa e persistita: stato, PDF, data
      // firma, bozza pulita. Quello che resta sono conseguenze (progetti, rinnovi,
      // task da template, evento per i workflow, contatti del gruppo).
      //
      // Erano attese, e l'attesa era doppia: updateDealStatus('accepted') esegue già
      // rinnovi, provvigioni, coda fatture, progetti e task; handleDealSigned rifà
      // fetchDealById, rinnovi, progetti e task, e l'unica cosa sua è l'evento
      // deal:signed. Con query da 3-8 secondi l'una la finestra restava a girare per
      // oltre un minuto a lavoro ormai finito, e chi accettava pensava fosse bloccata.
      // Il trigger deal_signed lato database è indipendente da questo evento e scatta
      // comunque, quindi i workflow partono anche se qui va storto.
      void (async () => {
        try {
          await useDealStore.getState().handleDealSigned(deal.id);
        } catch (e) {
          console.error('[useAcceptanceDraft] handleDealSigned failed (non-blocking):', e);
        }
        if (deal.client_id && cleanedNumbers.length > 0) {
          try {
            await saveGroupNumbersAsContacts(deal.client_id, cleanedNumbers);
          } catch (e) {
            console.error('[useAcceptanceDraft] saveGroupNumbersAsContacts failed (non-blocking):', e);
          }
        }
      })();

      return true;
    } catch (e) {
      console.error('[useAcceptanceDraft] complete failed:', e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Scarta la bozza (deal.acceptance_draft = null). */
  const discardDraft = async (dealId: string): Promise<boolean> => {
    setBusy(true);
    try {
      const { error } = await supabase.from('deals').update({ acceptance_draft: null }).eq('id', dealId);
      if (error) throw error;
      return true;
    } catch (e) {
      console.error('[useAcceptanceDraft] discardDraft failed:', e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, loadDraft, saveDraft, complete, discardDraft };
}
